import React, { useState, useRef, useEffect } from 'react';
import { 
  ChefHat, ShoppingCart, Utensils, Flame, Sparkles, 
  RotateCcw, Trophy, BookOpen, Info, MousePointerClick
} from 'lucide-react';

// นำเข้า SiteHeader และ Footer จากไฟล์เดิมของคุณ (ปรับ path ให้ตรงกับโปรเจกต์)
import SiteHeader from '../components/SiteHeader'; 
import Footer from '../components/Footer';

const LannaChefGamePage = () => {
  // --- Game Config ---
  const config = {
    game_title: 'เชฟน้อยล้านนา',
    primary: '#ea580c',
    secondary: '#a16207',
    bg: '#fef3e2'
  };

  // --- State ---
  const [currentStage, setCurrentStage] = useState(0); // 0-6 (7 หน้า)
  const [toasts, setToasts] = useState([]);
  
  // Market State
  const [basketItems, setBasketItems] = useState(new Set());
  
  // Prep State (พริกแกง)
  const [mortarItems, setMortarItems] = useState(new Set());
  const [poundCount, setPoundCount] = useState(0);
  
  // Cooking State
  const [cookingStep, setCookingStep] = useState(0);
  const [potHealth, setPotHealth] = useState(100);
  
  // Plating State
  const [platingPhase, setPlatingPhase] = useState(1); // 1: เส้น, 2: ราดน้ำ, 3: ตกแต่ง, 4: จบเกม
  const [brothLevel, setBrothLevel] = useState(0);
  const [garnishes, setGarnishes] = useState([]);
  const [activeGarnish, setActiveGarnish] = useState('🌿');
  const pourTimer = useRef(null);

  // --- Audio ---
  const audioCtx = useRef(null);
  const playSound = (type) => {
    if (!audioCtx.current) {
      audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = audioCtx.current;
    try {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);

      if (type === 'correct') {
        o.frequency.setValueAtTime(880, ctx.currentTime);
        o.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
        g.gain.setValueAtTime(0.15, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        o.start(); o.stop(ctx.currentTime + 0.3);
      } else if (type === 'wrong') {
        o.frequency.setValueAtTime(300, ctx.currentTime);
        o.frequency.setValueAtTime(200, ctx.currentTime + 0.15);
        o.type = 'sawtooth';
        g.gain.setValueAtTime(0.15, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        o.start(); o.stop(ctx.currentTime + 0.3);
      } else if (type === 'pound') {
        o.frequency.setValueAtTime(150, ctx.currentTime);
        o.type = 'triangle';
        g.gain.setValueAtTime(0.2, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        o.start(); o.stop(ctx.currentTime + 0.1);
      } else if (type === 'bubble') {
        o.frequency.setValueAtTime(200, ctx.currentTime);
        o.frequency.linearRampToValueAtTime(600, ctx.currentTime + 0.1);
        g.gain.setValueAtTime(0.1, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        o.start(); o.stop(ctx.currentTime + 0.2);
      } else if (type === 'pop') {
        o.frequency.setValueAtTime(600, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.05);
        g.gain.setValueAtTime(0.1, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0.01, ctx.currentTime + 0.05);
        o.start(); o.stop(ctx.currentTime + 0.05);
      }
    } catch (e) { console.error(e); }
  };

  const showToast = (msg, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 2000);
  };

  useEffect(() => {
    return () => clearInterval(pourTimer.current);
  }, []);

  // --- Handlers ---
  const handleMarketPick = (id, isCorrect, msg) => {
    if (basketItems.has(id)) return;
    if (isCorrect) {
      setBasketItems(prev => new Set(prev).add(id));
      playSound('correct');
      showToast('🌟 หยิบใส่ตะกร้าแล้ว!');
    } else {
      playSound('wrong');
      showToast(msg || '❌ อันนี้ไม่ได้ใช้นะ!', 'error');
    }
  };

  const handleMortarPick = (id, isCorrect, msg) => {
    if (mortarItems.has(id)) return;
    if (isCorrect) {
      setMortarItems(prev => new Set(prev).add(id));
      playSound('correct');
      showToast('🌟 ใส่ครกแล้ว!');
    } else {
      playSound('wrong');
      showToast(msg || '❌ ผิดสูตร! อันนี้ไม่ใส่พริกแกงนะ', 'error');
    }
  };

  const handlePound = () => {
    if (poundCount < 20) {
      setPoundCount(prev => prev + 1);
      playSound('pound');
      if (poundCount + 1 === 20) {
        playSound('correct');
        showToast('🌶️ โขลกพริกแกงเนียนกริบ!');
      }
    }
  };

  const handleCookingAction = (expectedStep) => {
    if (cookingStep === expectedStep) {
      setCookingStep(prev => prev + 1);
      playSound('bubble');
      showToast('🍲 ถูกต้อง! หอมฉุยเลย');
      if (expectedStep === 3) {
          setTimeout(() => playSound('correct'), 500);
      }
    } else {
      setPotHealth(prev => Math.max(0, prev - 25));
      playSound('wrong');
      showToast('🔥 ผิดลำดับ! ระวังหม้อไหม้นะ', 'error');
    }
  };

  const startPour = (e) => {
    if (e.type === 'mousedown' && e.button !== 0) return;
    if (platingPhase !== 2) return;
    playSound('bubble');
    pourTimer.current = setInterval(() => {
        setBrothLevel(prev => {
            if (prev >= 100) {
                clearInterval(pourTimer.current);
                return 100;
            }
            return prev + 1.5;
        });
    }, 30);
  };

  const stopPour = () => {
    if (platingPhase !== 2) return;
    clearInterval(pourTimer.current);
    
    if (brothLevel >= 70 && brothLevel <= 90) {
        playSound('correct');
        showToast('เป๊ะมาก! ราดน้ำเงี้ยวได้พอดีชาม', 'success');
        setPlatingPhase(3); 
    } else if (brothLevel > 90) {
        playSound('wrong');
        showToast('น้ำล้นชามแล้ว! เททิ้งแล้วกะจังหวะใหม่นะ', 'error');
        setBrothLevel(0); 
    } else if (brothLevel > 0) {
        showToast('น้ำเงี้ยวยังน้อยไป กดค้างราดเพิ่มอีกหน่อย', 'error');
    }
  };

  const handlePlateClick = (e) => {
    if (platingPhase !== 3) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const rotation = Math.floor(Math.random() * 360);
    
    setGarnishes(prev => [...prev, { id: Date.now(), x, y, emoji: activeGarnish, rotation }]);
    playSound('pop');
  };

  const resetGame = () => {
    setCurrentStage(0);
    setBasketItems(new Set());
    setMortarItems(new Set());
    setPoundCount(0);
    setCookingStep(0);
    setPotHealth(100);
    setPlatingPhase(1);
    setBrothLevel(0);
    setGarnishes([]);
  };

  // --- Render Pages ---
  const renderPage1 = () => (
    <div className="flex flex-col md:flex-row items-center justify-center gap-12 bg-white/80 p-12 md:p-16 rounded-3xl shadow-2xl max-w-5xl w-full border-4 border-orange-100 animate-slide-up">
      <div className="bg-gradient-to-br from-orange-500 to-yellow-500 w-48 h-48 md:w-64 md:h-64 rounded-full flex items-center justify-center shadow-2xl animate-bounce-slow border-8 border-white shrink-0">
        <ChefHat size={120} color="white" />
      </div>
      <div className="text-center md:text-left flex flex-col items-center md:items-start">
        <h1 className="text-5xl md:text-7xl font-black mb-4" style={{ color: config.primary }}>{config.game_title}</h1>
        <p className="text-2xl md:text-3xl font-semibold mb-8 text-gray-700">ภารกิจ "ขนมจีนน้ำเงี้ยว" แสนอร่อย</p>
        <button onClick={() => { setCurrentStage(1); playSound('correct'); }} className="px-12 py-5 bg-orange-600 hover:bg-orange-500 text-white rounded-full font-bold text-2xl shadow-xl hover:-translate-y-1 transition-all flex items-center gap-3">
          เริ่มเรียนรู้กันเลย <Utensils size={28} />
        </button>
      </div>
    </div>
  );

  const renderPage2 = () => (
    <div className="bg-white/90 p-10 md:p-14 rounded-3xl shadow-xl max-w-5xl w-full animate-slide-up text-center border-4 border-orange-100">
      <h2 className="text-4xl md:text-5xl font-black text-orange-600 mb-8 flex justify-center items-center gap-3"><BookOpen size={40}/> ตำราลับน้ำเงี้ยว</h2>
      <p className="text-xl mb-8 text-gray-700">น้ำเงี้ยวแท้ๆ ต้องมีวัตถุดิบสำคัญเหล่านี้ จำไว้ให้ดีล่ะ!</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-10">
        {[
          { emoji: '🐷', name: 'ซี่โครงหมู / หมูสับ', desc: 'ให้น้ำซุปหวานกลมกล่อม' },
          { emoji: '🍅', name: 'มะเขือเทศส้ม', desc: 'ให้รสเปรี้ยวอมหวาน' },
          { emoji: '🌺', name: 'ดอกงิ้วตากแห้ง', desc: 'เอกลักษณ์ล้านนา กรุบกรอบ' },
          { emoji: '🩸', name: 'เลือดหมูไก่ต้ม', desc: 'เพิ่มเนื้อสัมผัส' },
          { emoji: '🌶️', name: 'พริกแกงน้ำเงี้ยว', desc: 'เผ็ดร้อน หอมเครื่องเทศ' },
          { emoji: '🍝', name: 'เส้นขนมจีน', desc: 'กินคู่กันอร่อยที่สุด' },
        ].map((item, idx) => (
          <div key={idx} className="bg-orange-50 p-6 rounded-2xl border-2 border-orange-200 shadow-sm hover:shadow-md transition-shadow">
            <span className="text-5xl block mb-2">{item.emoji}</span>
            <h3 className="font-bold text-xl text-orange-800">{item.name}</h3>
            <p className="text-sm text-gray-600 mt-2">{item.desc}</p>
          </div>
        ))}
      </div>
      <button onClick={() => setCurrentStage(2)} className="bg-orange-600 hover:bg-orange-500 text-white px-10 py-4 rounded-full font-bold text-2xl shadow-lg transition-all hover:scale-105">
        เข้าใจแล้ว! หน้าต่อไป ➡️
      </button>
    </div>
  );

  const renderPage3 = () => (
    <div className="bg-white/90 p-10 md:p-14 rounded-3xl shadow-xl max-w-3xl w-full animate-slide-up border-4 border-orange-100">
      <h2 className="text-4xl font-black text-orange-600 mb-8 text-center flex justify-center items-center gap-3"><Info size={40}/> วิธีการเล่นเกม</h2>
      <ul className="space-y-6 text-xl text-gray-700 font-medium mb-10">
        <li className="flex items-center gap-4 bg-orange-50 p-4 rounded-xl"><ShoppingCart className="text-orange-500 flex-shrink-0"/> 1. จ่ายตลาด: เลือกวัตถุดิบหลัก 6 อย่างให้ถูกต้อง</li>
        <li className="flex items-center gap-4 bg-orange-50 p-4 rounded-xl"><Flame className="text-orange-500 flex-shrink-0"/> 2. เตรียมของ: เลือกส่วนผสมใส่ครก แล้วคลิกเพื่อโขลกพริกแกง</li>
        <li className="flex items-center gap-4 bg-orange-50 p-4 rounded-xl"><Utensils className="text-orange-500 flex-shrink-0"/> 3. ปรุงอาหาร: ใส่วัตถุดิบลงหม้อตามลำดับให้ถูกต้อง</li>
        <li className="flex items-center gap-4 bg-orange-50 p-4 rounded-xl"><Sparkles className="text-orange-500 flex-shrink-0"/> 4. จัดจาน: กะจังหวะราดน้ำเงี้ยวให้พอดี และตกแต่งจานเอง!</li>
      </ul>
      <div className="text-center">
        <button onClick={() => setCurrentStage(3)} className="bg-green-600 hover:bg-green-500 text-white px-12 py-5 rounded-full font-black text-2xl shadow-xl transition-all hover:scale-105 animate-pulse">
          เริ่มภารกิจแรกเลย! 🚀
        </button>
      </div>
    </div>
  );

  const renderPage4 = () => {
    const items = [
      { id: 'ribs', emoji: '🐷', name: 'ซี่โครงหมู', correct: true },
      { id: 'tomato', emoji: '🍅', name: 'มะเขือเทศ', correct: true },
      { id: 'ngiew', emoji: '🌺', name: 'ดอกงิ้ว', correct: true },
      { id: 'pasta', emoji: '🍝', name: 'สปาเก็ตตี้', correct: false, msg: 'ผิดเมนูแล้ว! ต้องขนมจีนสิ' },
      { id: 'blood', emoji: '🩸', name: 'ก้อนเลือด', correct: true },
      { id: 'cheese', emoji: '🧀', name: 'ชีส', correct: false, msg: 'น้ำเงี้ยวใส่ชีสไม่ได้นะ!' },
      { id: 'paste', emoji: '🌶️', name: 'พริกแกง', correct: true },
      { id: 'noodle', emoji: '🍜', name: 'ขนมจีน', correct: true },
    ];

    return (
      <div className="w-full max-w-5xl mx-auto bg-white/80 p-10 rounded-3xl shadow-xl border-4 border-orange-100 animate-slide-up">
        <div className="flex flex-col md:flex-row justify-between items-center mb-8 border-b-2 border-orange-200 pb-6 gap-4">
          <h2 className="text-4xl font-black text-orange-600 flex items-center gap-3"><ShoppingCart/> ตลาดสดล้านนา</h2>
          <div className="w-full md:w-1/3 bg-gray-200 h-8 rounded-full overflow-hidden shadow-inner relative border-2 border-gray-300">
            <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${(basketItems.size / 6) * 100}%` }}></div>
            <p className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-800">ได้ของ {basketItems.size}/6 อย่าง</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {items.map(item => (
            <div key={item.id} onClick={() => handleMarketPick(item.id, item.correct, item.msg)}
              className={`p-6 rounded-3xl text-center cursor-pointer transition-all border-4 
                ${basketItems.has(item.id) ? 'bg-green-100 border-green-500 opacity-60 scale-95' : 'bg-white hover:border-orange-400 hover:shadow-xl hover:-translate-y-2'}`}>
              <span className="text-7xl block mb-4">{item.emoji}</span>
              <p className="text-xl font-bold text-gray-700">{item.name}</p>
            </div>
          ))}
        </div>
        {basketItems.size >= 6 && (
          <div className="mt-10 text-center animate-bounce">
            <button onClick={() => setCurrentStage(4)} className="bg-orange-600 hover:bg-orange-500 text-white px-10 py-4 rounded-full font-bold text-2xl shadow-xl">
              ของครบแล้ว! ไปทำอาหารกัน ➡️
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderPage5 = () => {
    const pasteIngredientsList = [
      { id: 'chili', emoji: '🌶️', name: 'พริกแห้ง', correct: true },
      { id: 'garlic', emoji: '🧄', name: 'กระเทียม', correct: true },
      { id: 'shallot', emoji: '🧅', name: 'หอมแดง', correct: true },
      { id: 'tuanao', emoji: '🍘', name: 'ถั่วเน่าแข็บ', correct: true },
      { id: 'kapi', emoji: '🥄', name: 'กะปิ', correct: true },
      { id: 'basil', emoji: '🌿', name: 'ใบกะเพรา', correct: false, msg: 'นี่มันผัดกะเพราแล้ว!' },
    ];
    const isReadyToPound = mortarItems.size >= 5;

    return (
      <div className="text-center bg-white/90 p-12 rounded-3xl shadow-2xl max-w-4xl w-full border-4 border-orange-100 animate-slide-up">
        <h2 className="text-4xl font-black mb-8 text-orange-700 flex justify-center items-center gap-3">
          <Flame size={40} className="animate-pulse" /> เตรียมพริกแกง
        </h2>
        {!isReadyToPound ? (
          <div className="animate-fade-in">
            <p className="text-2xl mb-8 text-gray-700 font-medium">เลือก <span className="text-orange-600 font-bold">"ส่วนผสมพริกแกง"</span> ใส่ครกให้ครบ!</p>
            <div className="flex flex-col md:flex-row gap-10 items-center justify-center mb-8">
                <div className="w-56 h-48 bg-stone-700 rounded-b-[90px] relative flex flex-col items-center justify-end pb-6 border-t-[12px] border-stone-800 shadow-2xl shrink-0">
                    <span className="text-white/50 text-6xl absolute top-6">🥣</span>
                    <span className="text-white font-bold text-xl z-10 bg-black/30 px-4 py-1 rounded-full">ใส่แล้ว {mortarItems.size}/5</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {pasteIngredientsList.map(item => (
                    <button key={item.id} onClick={() => handleMortarPick(item.id, item.correct, item.msg)} disabled={mortarItems.has(item.id)}
                      className={`p-4 rounded-2xl font-bold text-lg transition-all border-4 flex flex-col items-center justify-center h-32 w-32
                        ${mortarItems.has(item.id) ? 'bg-green-50 border-green-400 text-green-700 opacity-50 scale-95' : 'bg-white hover:border-orange-400 hover:shadow-lg hover:-translate-y-2'}`}>
                      <span className="text-4xl mb-2">{item.emoji}</span><span>{item.name}</span>
                    </button>
                  ))}
                </div>
            </div>
          </div>
        ) : (
          <div className="animate-scale-in">
             <p className="text-2xl mb-8 text-gray-700 font-medium">เครื่องแกงพร้อม! <span className="text-red-600 font-bold">คลิกรัวๆ</span> เพื่อโขลกให้ละเอียด!</p>
             <div className={`w-64 h-48 bg-stone-700 rounded-b-[100px] mx-auto relative flex items-center justify-center border-t-8 border-stone-800 shadow-[inset_0_-20px_40px_rgba(0,0,0,0.5)] ${poundCount > 0 && poundCount < 20 ? 'animate-shake' : ''}`}>
                 <span className="text-white font-bold text-3xl drop-shadow-md z-10">{poundCount < 20 ? 'โขลกเลย!' : 'เนียนละเอียดมาก!'}</span>
                 <div className={`absolute top-[-70px] w-12 h-40 bg-amber-800 rounded-full shadow-2xl transition-transform duration-75 origin-bottom ${poundCount > 0 && poundCount < 20 && poundCount % 2 === 0 ? 'translate-y-12 rotate-[-5deg]' : 'rotate-[5deg]'}`}></div>
             </div>
             <div className="mt-16 flex flex-col items-center">
                <button onClick={handlePound} disabled={poundCount >= 20} className={`w-32 h-32 md:w-40 md:h-40 rounded-full font-black text-4xl shadow-xl transition-all border-4 border-white mb-6 ${poundCount >= 20 ? 'bg-gray-400 text-white cursor-not-allowed border-gray-300' : 'bg-red-600 text-white hover:bg-red-500 active:scale-95 active:shadow-inner'}`}>
                  {poundCount >= 20 ? 'เสร็จ!' : 'ตำ!'}
                </button>
                <div className="w-full max-w-md mx-auto">
                  <p className="text-gray-600 font-bold mb-2">ความละเอียด: {poundCount}/20</p>
                  <div className="w-full bg-gray-200 h-6 rounded-full overflow-hidden shadow-inner border-2 border-gray-300">
                    <div className="h-full bg-gradient-to-r from-red-400 to-red-600 transition-all duration-200" style={{ width: `${(poundCount / 20) * 100}%` }}></div>
                  </div>
                </div>
             </div>
             {poundCount >= 20 && (
                 <button onClick={() => setCurrentStage(5)} className="mt-12 bg-orange-600 text-white px-12 py-5 rounded-full font-bold text-2xl shadow-xl hover:-translate-y-1 transition-all animate-bounce">
                   พริกแกงพร้อม! ไปหน้าเตา ➡️
                 </button>
             )}
          </div>
        )}
      </div>
    );
  };

  const renderPage6 = () => {
    const steps = [
      { name: '1. ต้มน้ำต้มกระดูก', icon: '💧' },
      { name: '2. ผัดพริกแกงใส่หมู', icon: '🌶️' },
      { name: '3. ใส่ดอกงิ้ว/เลือด', icon: '🌺' },
      { name: '4. เคี่ยวจนเปื่อย', icon: '⏳' }
    ];

    return (
      <div className="w-full max-w-4xl mx-auto bg-white/90 p-12 rounded-3xl shadow-xl border-4 border-orange-100 animate-slide-up text-center">
        <h2 className="text-4xl font-black mb-4 text-orange-700"><Flame className="inline mb-2"/> หน้าเตาไฟ</h2>
        <p className="text-xl mb-8 text-gray-600">คลิกเลือกขั้นตอนให้ถูกต้องตามลำดับ!</p>

        <div className="relative w-80 h-64 mx-auto mb-10">
          <div className={`absolute bottom-0 w-full h-48 bg-stone-800 rounded-b-full rounded-t-xl border-t-8 border-stone-900 shadow-2xl flex items-center justify-center overflow-hidden transition-all duration-500`}>
             <div className="absolute bottom-0 w-full bg-orange-600 transition-all duration-1000 opacity-80" style={{ height: `${(cookingStep / 4) * 100}%` }}></div>
             <span className="relative z-10 text-white font-bold text-2xl drop-shadow-md">
               {cookingStep === 0 ? 'หม้อเปล่า' : cookingStep === 1 ? 'น้ำซุปเดือด' : cookingStep === 2 ? 'หอมพริกแกง' : cookingStep === 3 ? 'ใส่เครื่องครบ' : 'น้ำเงี้ยวพร้อมเสิร์ฟ!'}
             </span>
          </div>
          <div className="absolute -right-8 top-10 h-32 w-6 bg-gray-200 rounded-full border-2 border-gray-400 overflow-hidden">
             <div className="w-full bg-red-500 transition-all duration-300 absolute bottom-0" style={{ height: `${potHealth}%` }}></div>
          </div>
        </div>

        {cookingStep < 4 ? (
          <div className="grid grid-cols-2 gap-4">
            {steps.map((step, idx) => (
              <button key={idx} onClick={() => handleCookingAction(idx)} disabled={cookingStep > idx || potHealth <= 0}
                className={`p-4 rounded-2xl font-bold text-xl transition-all border-4 hover:-translate-y-1 active:scale-95
                  ${cookingStep > idx ? 'bg-green-100 border-green-500 text-green-700 opacity-50 cursor-not-allowed' : 'bg-orange-100 border-orange-300 text-orange-800 hover:bg-orange-200 hover:shadow-md'}`}>
                <span className="text-3xl mr-2">{step.icon}</span> {step.name}
              </button>
            ))}
          </div>
        ) : (
          <div className="animate-bounce mt-8">
            <button onClick={() => setCurrentStage(6)} className="bg-orange-600 text-white px-12 py-5 rounded-full font-black text-2xl shadow-xl hover:-translate-y-1 transition-all">
              น้ำเงี้ยวสุกแล้ว! ไปจัดจานกัน 🍜
            </button>
          </div>
        )}
        {potHealth <= 0 && (
          <div className="mt-6 text-red-600 font-bold text-xl animate-pulse">
            🔥 หม้อไหม้แล้ว! <button onClick={() => {setCookingStep(0); setPotHealth(100);}} className="underline">เริ่มปรุงใหม่</button>
          </div>
        )}
      </div>
    );
  };

  const renderPage7 = () => {
    const garnishesList = [
      { id: 'coriander', emoji: '🌿', name: 'ผักชี' },
      { id: 'garlic', emoji: '🧄', name: 'กระเทียมเจียว' },
      { id: 'lime', emoji: '🍋', name: 'มะนาว' },
      { id: 'chili', emoji: '🌶️', name: 'พริกทอด' },
    ];

    if (platingPhase === 4) {
      return (
        <div className="text-center bg-white/90 p-12 md:p-16 rounded-3xl shadow-2xl max-w-4xl w-full border-4 border-yellow-200 animate-scale-in flex flex-col items-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-yellow-400 blur-3xl opacity-40 rounded-full"></div>
            <Trophy size={140} className="mx-auto text-yellow-500 drop-shadow-2xl relative z-10 animate-bounce-slow" />
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-orange-600 mb-4 drop-shadow-sm">สุดยอดมาสเตอร์เชฟ!</h1>
          <p className="text-2xl mt-2 text-gray-700 font-bold mb-10">ขนมจีนน้ำเงี้ยวฝีมือคุณ อร่อยขนาดเจ้า! 🍜✨</p>
          <button onClick={resetGame} className="flex items-center gap-3 bg-stone-800 hover:bg-stone-700 text-white px-10 py-5 rounded-full font-bold text-2xl shadow-xl hover:-translate-y-1 transition-all">
            <RotateCcw size={28} /> กลับไปเล่นอีกครั้ง
          </button>
        </div>
      );
    }

    return (
      <div className="text-center bg-white/90 p-8 md:p-12 rounded-3xl shadow-2xl max-w-4xl w-full border-4 border-orange-100 animate-slide-up flex flex-col items-center">
        <h2 className="text-4xl font-black mb-6 text-orange-600">✨ ขั้นตอนสุดท้าย: จัดจาน</h2>
        <p className="text-xl font-bold mb-6 text-gray-700 bg-orange-50 px-6 py-2 rounded-full border border-orange-200">
          {platingPhase === 1 && 'ขั้นที่ 1: เตรียมเส้นขนมจีนลงชาม'}
          {platingPhase === 2 && 'ขั้นที่ 2: กะจังหวะราดน้ำเงี้ยวให้พอดี'}
          {platingPhase === 3 && 'ขั้นที่ 3: ตกแต่งจานได้ตามใจชอบ!'}
        </p>

        <div className="flex flex-col md:flex-row gap-12 items-center w-full justify-center">
            <div className={`relative w-72 h-72 md:w-80 md:h-80 bg-blue-50 rounded-full border-[12px] border-blue-200 shadow-inner overflow-hidden flex-shrink-0 transition-all duration-300
                    ${platingPhase === 3 ? 'cursor-crosshair hover:border-blue-300 hover:shadow-[0_0_20px_rgba(59,130,246,0.3)]' : ''}`}
                onClick={handlePlateClick}>
                {platingPhase >= 2 && <span className="absolute inset-0 flex items-center justify-center text-[10rem] drop-shadow-md z-10 pointer-events-none">🍝</span>}
                {(platingPhase === 2 || platingPhase === 3) && (
                    <div className="absolute bottom-0 w-full bg-orange-600/85 transition-all duration-75 z-20 pointer-events-none" style={{ height: `${brothLevel}%` }}></div>
                )}
                {garnishes.map(g => (
                    <span key={g.id} className="absolute text-4xl z-30 pointer-events-none" style={{ left: g.x, top: g.y, transform: `translate(-50%, -50%) rotate(${g.rotation}deg)` }}>
                        {g.emoji}
                    </span>
                ))}
            </div>

            <div className="flex flex-col items-center min-w-[250px]">
                {platingPhase === 1 && (
                    <button onClick={() => { setPlatingPhase(2); playSound('correct'); }} className="px-8 py-4 bg-orange-100 hover:bg-orange-200 rounded-full font-bold text-2xl border-4 border-orange-300 text-orange-800 shadow-md transition-transform hover:scale-105 active:scale-95">
                        ใส่เส้นขนมจีน 🍜
                    </button>
                )}
                {platingPhase === 2 && (
                    <div className="flex flex-col items-center w-full animate-fade-in">
                        <div className="w-full max-w-[200px] mb-4">
                            <div className="relative w-full bg-gray-200 h-10 rounded-full overflow-hidden border-2 border-gray-400 shadow-inner">
                                <div className="absolute left-[70%] w-[20%] h-full bg-green-300/50 z-10 border-l-2 border-r-2 border-green-600"></div>
                                <div className={`absolute left-0 top-0 h-full transition-all duration-75 ${brothLevel > 90 ? 'bg-red-500' : 'bg-orange-500'}`} style={{ width: `${brothLevel}%` }}></div>
                            </div>
                            <div className="flex justify-between text-xs font-bold text-gray-500 mt-1 px-2">
                                <span>ว่างเปล่า</span><span className="text-green-600">พอดีเป๊ะ!</span><span className="text-red-600">ล้น!</span>
                            </div>
                        </div>
                        <button onMouseDown={startPour} onMouseUp={stopPour} onMouseLeave={stopPour} onTouchStart={startPour} onTouchEnd={stopPour}
                            className="px-8 py-5 bg-orange-500 text-white rounded-full font-bold text-xl shadow-[0_8px_0_#c2410c] active:shadow-[0_0px_0_#c2410c] active:translate-y-2 select-none touch-none flex flex-col items-center">
                            <Flame size={28} className="mb-1" /> กดค้างเพื่อราดน้ำ
                        </button>
                    </div>
                )}
                {platingPhase === 3 && (
                    <div className="flex flex-col items-center animate-fade-in w-full">
                        <p className="text-gray-600 font-bold mb-4 flex items-center gap-2"><MousePointerClick size={20}/> เลือกของแล้วคลิกที่จาน!</p>
                        <div className="grid grid-cols-2 gap-3 mb-6 w-full">
                            {garnishesList.map(g => (
                                <button key={g.id} onClick={() => setActiveGarnish(g.emoji)}
                                    className={`p-3 rounded-xl font-bold border-4 transition-all flex flex-col items-center
                                        ${activeGarnish === g.emoji ? 'bg-green-100 border-green-500 scale-105 shadow-md' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                                    <span className="text-3xl">{g.emoji}</span><span className="text-sm mt-1">{g.name}</span>
                                </button>
                            ))}
                        </div>
                        <div className="bg-gray-100 rounded-full px-6 py-2 font-bold text-gray-700 border border-gray-300 mb-6">
                            ใส่ท็อปปิ้งแล้ว: {garnishes.length} ชิ้น
                        </div>
                        {garnishes.length >= 5 && (
                            <button onClick={() => { setPlatingPhase(4); playSound('correct'); }} className="px-10 py-4 bg-green-600 hover:bg-green-500 text-white rounded-full font-black text-2xl shadow-xl hover:-translate-y-1 transition-all animate-bounce">
                                เสิร์ฟได้เลย! ✨
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
      </div>
    );
  };

  // --- Main Structure ---
  return (
    <div className="min-h-screen flex flex-col relative font-sans" style={{ backgroundColor: config.bg, fontFamily: "'Kanit', sans-serif" }}>
      
      {/* 1. Header จากไฟล์เดิมของคุณ */}
      <SiteHeader />

      {/* 2. Main Content Area */}
      <main className="flex-1 relative overflow-hidden flex flex-col items-center justify-center p-6 md:p-12 z-10 w-full">
        
        {/* Background Decor */}
        <div className="absolute top-[-10%] left-[-5%] w-96 h-96 bg-orange-300/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-[-10%] right-[-5%] w-[30rem] h-[30rem] bg-yellow-300/20 rounded-full blur-3xl pointer-events-none"></div>

        {/* Toasts */}
        <div className="fixed top-24 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-3 w-full max-w-md pointer-events-none">
          {toasts.map(t => (
            <div key={t.id} className={`px-8 py-4 rounded-2xl text-white font-bold text-lg shadow-2xl text-center border-2 border-white/20 backdrop-blur-md animate-slide-up
              ${t.type === 'success' ? 'bg-green-600/90' : 'bg-red-600/90'}`}>
              {t.msg}
            </div>
          ))}
        </div>

        {/* Current Game Stage */}
        <div className="w-full max-w-6xl mx-auto flex justify-center items-center">
            {currentStage === 0 && renderPage1()}
            {currentStage === 1 && renderPage2()}
            {currentStage === 2 && renderPage3()}
            {currentStage === 3 && renderPage4()}
            {currentStage === 4 && renderPage5()}
            {currentStage === 5 && renderPage6()}
            {currentStage === 6 && renderPage7()}
        </div>

      </main>

      {/* 3. Footer จากไฟล์เดิมของคุณ */}
      <Footer />

      {/* Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Kanit:wght@300;400;500;600;800;900&display=swap');
        @keyframes slide-up { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scale-in { from { transform: scale(0.9); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes shake { 0%, 100% { transform: rotate(0); } 25% { transform: rotate(-3deg); } 75% { transform: rotate(3deg); } }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-15px); } }
        .animate-slide-up { animation: slide-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-fade-in { animation: fade-in 0.5s ease-out forwards; }
        .animate-scale-in { animation: scale-in 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        .animate-shake { animation: shake 0.15s ease-in-out infinite; }
        .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
      `}} />
    </div>
  );
};

export default LannaChefGamePage;